import { SentryService } from '@/services/sentry';
import message from 'antd/es/message';

const DEFAULT_ERROR_MESSAGES_BY_HTTP_STATUS: Record<number, string> = {
  409: 'Information has changed since you first loaded this page. Please refresh the page and retry your action.',
  403: 'Missing permission',
};

const getErrorMessage = (error: any, defaultMessage?: string) => {
  if (!error) {
    return defaultMessage || '';
  }

  if (typeof error === 'string') {
    return error;
  }

  const msg = error.displayMessage || error.textResponse || error.message;

  if (
    !msg &&
    error?.response?.status &&
    DEFAULT_ERROR_MESSAGES_BY_HTTP_STATUS[error.response.status]
  ) {
    return DEFAULT_ERROR_MESSAGES_BY_HTTP_STATUS[error.response.status];
  }

  if (defaultMessage) {
    return msg || defaultMessage;
  }

  return msg || '';
};

const parseServiceErrorMessage = (msg: string): string => {
  if (typeof msg !== 'string') {
    // eslint-disable-next-line no-console
    console.error('Message must be a string', msg);
    return msg;
  }

  return msg;
};

type HandleErrorOptions = {
  displayToast?: boolean;
  /** This is the message displayed even if the error includes an error message */
  toastMessage?: string;
  /** This is a fallback message displayed if the error doesn't include an error message and `toastMessage` is not set */
  toastFallbackMessage?: string;
  rethrowError?: boolean;
  sendToSentry?: boolean;
  parseServiceError?: boolean;
  skipIfPermissionIssue?: boolean;
};

export const handleError = (
  error: any,
  {
    displayToast = true,
    toastMessage,
    toastFallbackMessage = 'Unexpected Error',
    rethrowError,
    sendToSentry = true,
    parseServiceError = false,
    skipIfPermissionIssue = false,
  }: HandleErrorOptions = {},
) => {
  if (skipIfPermissionIssue && error?.status === 403) {
    return;
  }

  if (displayToast) {
    let errorMessage = toastMessage ?? getErrorMessage(error, toastFallbackMessage);

    if (parseServiceError) {
      errorMessage = parseServiceErrorMessage(errorMessage);
    }

    message.error(errorMessage);
  }
  if (sendToSentry) {
    SentryService.trackError(JSON.stringify(error));
  }

  if (rethrowError) {
    throw error;
  }
};
